import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';

const TOOLTIP_SX = { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 };

const INSIGHT_ICONS = {
    'Peak month': { icon: 'eva:trending-up-fill', color: '#10b981', bgColor: '#d1fae5' },
    'Slow period': { icon: 'eva:trending-down-fill', color: '#ef4444', bgColor: '#fee2e2' },
    'Best converting': { icon: 'eva:star-fill', color: '#f59e0b', bgColor: '#fef3c7' },
};

export default function SeasonalityCard({ insights, title }) {
    const getInsightConfig = (insightType) => INSIGHT_ICONS[insightType] || {
            icon: 'eva:star-fill',
            color: '#3b82f6',
            bgColor: '#dbeafe',
        };

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                mb: 3,
            }}
        >
            <Tooltip title="Seasonal patterns in lead volume, conversion rates and peak performance periods." placement="bottom" arrow
                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help', mb: 3 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                </Box>
            </Tooltip>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 2,
                }}
            >
                {insights.map((item, index) => {
                    const config = getInsightConfig(item.insight);

                    return (
                        <Box
                            key={index}
                            sx={{
                                p: 2.5,
                                borderRadius: '12px',
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        bgcolor: config.bgColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: config.color,
                                    }}
                                >
                                    <Iconify icon={config.icon} width={18} height={18} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#64748b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    {item.insight}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: '#1e293b',
                                }}
                            >
                                {item.value}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    color: '#64748b',
                                    lineHeight: 1.5,
                                }}
                            >
                                {item.description}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {insights.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No seasonality insights available
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

SeasonalityCard.propTypes = {
    insights: PropTypes.arrayOf(
        PropTypes.shape({
            insight: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
        })
    ).isRequired,
    title: PropTypes.string,
};

SeasonalityCard.defaultProps = {
    title: 'Seasonality Insights',
};
